import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

// Shop admin: shop orders, coupons and shop settings
const shopAdminApis = createApi({
    reducerPath: "shopAdminApis",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: [
        "ShopOrders",
        "ShopOrder",
        "Coupons",
        "ShopSettings",
        "Affiliates",
        "Affiliate",
        "AffiliateSettings",
        "Withdrawals",
        "PayoutBatches",
        "MilestoneTiers",
        "MilestoneRewards",
    ],
    endpoints: (build) => ({
        getShopOrders: build.query({
            query: ({ pageNo, limit, keywords, status, from, to, sortBy }) => ({
                url: "/shop-admin/orders",
                params: {
                    pageNo,
                    limit,
                    keywords: keywords || "",
                    status: status || "all",
                    from: from || "",
                    to: to || "",
                    sortBy,
                },
            }),
            transformResponse: (r) => r?.result || { count: 0, results: [], stats: {} },
            providesTags: ["ShopOrders"],
        }),
        getShopOrder: build.query({
            query: (id) => ({ url: `/shop-admin/orders/${id}` }),
            transformResponse: (r) => r?.result,
            providesTags: ["ShopOrder"],
        }),
        cancelShopOrder: build.mutation({
            query: ({ id, reason }) => ({ url: `/shop-admin/orders/${id}/cancel`, method: "POST", body: { reason } }),
            transformResponse: (r) => r?.result,
            invalidatesTags: ["ShopOrders", "ShopOrder", "Coupons"],
        }),
        shipShopOrder: build.mutation({
            query: (id) => ({ url: `/shop-admin/orders/${id}/ship`, method: "POST", body: {} }),
            transformResponse: (r) => r?.result,
            invalidatesTags: ["ShopOrders", "ShopOrder"],
        }),
        getCoupons: build.query({
            query: ({ keywords, status } = {}) => ({
                url: "/shop-admin/coupons",
                params: { keywords: keywords || "", status: status || "all" },
            }),
            transformResponse: (r) => r?.result || [],
            providesTags: ["Coupons"],
        }),
        createCoupon: build.mutation({
            query: (body) => ({ url: "/shop-admin/coupons", method: "POST", body }),
            invalidatesTags: ["Coupons"],
        }),
        updateCoupon: build.mutation({
            query: ({ id, ...body }) => ({ url: `/shop-admin/coupons/${id}`, method: "PUT", body }),
            invalidatesTags: ["Coupons"],
        }),
        deleteCoupon: build.mutation({
            query: (id) => ({ url: `/shop-admin/coupons/${id}`, method: "DELETE" }),
            invalidatesTags: ["Coupons"],
        }),
        getShopSettings: build.query({
            query: () => ({ url: "/shop-admin/settings" }),
            transformResponse: (r) => r?.result,
            providesTags: ["ShopSettings"],
        }),
        updateShopSettings: build.mutation({
            query: (body) => ({ url: "/shop-admin/settings", method: "PUT", body }),
            invalidatesTags: ["ShopSettings"],
        }),
        // Affiliate program
        getAffiliateDashboard: build.query({
            query: () => ({ url: "/shop-admin/affiliate/dashboard" }),
            transformResponse: (r) => r?.result,
            providesTags: ["Affiliates"],
        }),
        getAffiliates: build.query({
            query: ({ page, limit, keywords, flagged }) => ({
                url: "/shop-admin/affiliate/affiliates",
                params: { page, limit, keywords: keywords || "", ...(flagged ? { flagged: true } : {}) },
            }),
            transformResponse: (r) => r?.result || { count: 0, results: [] },
            providesTags: ["Affiliates"],
        }),
        getAffiliate: build.query({
            query: (id) => ({ url: `/shop-admin/affiliate/affiliates/${id}` }),
            transformResponse: (r) => r?.result,
            providesTags: ["Affiliate"],
        }),
        adjustAffiliateWallet: build.mutation({
            query: ({ id, amount, note }) => ({
                url: `/shop-admin/affiliate/affiliates/${id}/adjust`,
                method: "POST",
                body: { amount, note },
            }),
            invalidatesTags: ["Affiliates", "Affiliate"],
        }),
        setAffiliateStatus: build.mutation({
            query: ({ id, status, note }) => ({
                url: `/shop-admin/affiliate/affiliates/${id}/status`,
                method: "POST",
                body: { status, note },
            }),
            invalidatesTags: ["Affiliates", "Affiliate"],
        }),
        getCommissions: build.query({
            query: ({ page, limit, keywords, status, from, to }) => ({
                url: "/shop-admin/affiliate/commissions",
                params: {
                    page,
                    limit,
                    keywords: keywords || "",
                    status: status || "all",
                    ...(from ? { from } : {}),
                    ...(to ? { to } : {}),
                },
            }),
            transformResponse: (r) => r?.result || { count: 0, results: [] },
            providesTags: ["Affiliates"],
        }),
        getAffiliateSettings: build.query({
            query: () => ({ url: "/shop-admin/affiliate/settings" }),
            transformResponse: (r) => r?.result,
            providesTags: ["AffiliateSettings"],
        }),
        updateAffiliateSettings: build.mutation({
            query: (body) => ({ url: "/shop-admin/affiliate/settings", method: "PUT", body }),
            invalidatesTags: ["AffiliateSettings", "Affiliates"],
        }),
        // Weekly manual referral payouts
        getWithdrawals: build.query({
            query: ({ page, limit, keywords, status }) => ({
                url: "/shop-admin/affiliate/withdrawals",
                params: { page, limit, keywords: keywords || "", status: status || "all" },
            }),
            transformResponse: (r) => r?.result || { count: 0, requested_amount: 0, in_batch_amount: 0, results: [] },
            providesTags: ["Withdrawals"],
        }),
        createWeeklyPayoutBatch: build.mutation({
            query: () => ({ url: "/shop-admin/affiliate/payout-batches", method: "POST", body: {} }),
            transformResponse: (r) => r?.result,
            invalidatesTags: ["Withdrawals", "PayoutBatches", "Affiliates", "Affiliate"],
        }),
        getPayoutBatches: build.query({
            query: () => ({ url: "/shop-admin/affiliate/payout-batches" }),
            transformResponse: (r) => r?.result || [],
            providesTags: ["PayoutBatches"],
        }),
        getPayoutBatch: build.query({
            query: (id) => ({ url: `/shop-admin/affiliate/payout-batches/${id}` }),
            transformResponse: (r) => r?.result,
            providesTags: ["PayoutBatches"],
        }),
        rejectWithdrawal: build.mutation({
            query: ({ id, reason }) => ({
                url: `/shop-admin/affiliate/withdrawals/${id}/reject`,
                method: "POST",
                body: { reason },
            }),
            invalidatesTags: ["Withdrawals", "PayoutBatches", "Affiliates", "Affiliate"],
        }),
        markWithdrawalPaid: build.mutation({
            query: ({ batchId, withdrawalId, utr }) => ({
                url: `/shop-admin/affiliate/payout-batches/${batchId}/withdrawals/${withdrawalId}/paid`,
                method: "POST",
                body: { utr },
            }),
            invalidatesTags: ["Withdrawals", "PayoutBatches", "Affiliates", "Affiliate"],
        }),
        failWithdrawal: build.mutation({
            query: ({ batchId, withdrawalId, reason }) => ({
                url: `/shop-admin/affiliate/payout-batches/${batchId}/withdrawals/${withdrawalId}/failed`,
                method: "POST",
                body: { reason },
            }),
            invalidatesTags: ["Withdrawals", "PayoutBatches", "Affiliates", "Affiliate"],
        }),
        resolveLegacyWithdrawal: build.mutation({
            query: ({ id, ...body }) => ({
                url: `/shop-admin/affiliate/withdrawals/${id}/resolve-legacy`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["Withdrawals", "PayoutBatches", "Affiliates", "Affiliate"],
        }),
        // Buyer order milestones
        getMilestoneTiers: build.query({
            query: () => ({ url: "/shop-admin/milestones/tiers" }),
            transformResponse: (r) => r?.result || [],
            providesTags: ["MilestoneTiers"],
        }),
        createMilestoneTier: build.mutation({
            query: (body) => ({ url: "/shop-admin/milestones/tiers", method: "POST", body }),
            invalidatesTags: ["MilestoneTiers"],
        }),
        updateMilestoneTier: build.mutation({
            query: ({ id, ...body }) => ({ url: `/shop-admin/milestones/tiers/${id}`, method: "PUT", body }),
            invalidatesTags: ["MilestoneTiers"],
        }),
        deleteMilestoneTier: build.mutation({
            query: (id) => ({ url: `/shop-admin/milestones/tiers/${id}`, method: "DELETE" }),
            invalidatesTags: ["MilestoneTiers"],
        }),
        getMilestoneRewards: build.query({
            query: ({ page, limit, keywords, status }) => ({
                url: "/shop-admin/milestones/rewards",
                params: { page, limit, keywords: keywords || "", status: status || "all" },
            }),
            transformResponse: (r) => r?.result || { count: 0, pending: 0, results: [] },
            providesTags: ["MilestoneRewards"],
        }),
        updateMilestoneReward: build.mutation({
            query: ({ id, status, note }) => ({
                url: `/shop-admin/milestones/rewards/${id}`,
                method: "PUT",
                body: { status, note },
            }),
            invalidatesTags: ["MilestoneRewards", "MilestoneTiers"],
        }),
    }),
});

export const {
    useGetShopOrdersQuery,
    useGetShopOrderQuery,
    useCancelShopOrderMutation,
    useShipShopOrderMutation,
    useGetCouponsQuery,
    useCreateCouponMutation,
    useUpdateCouponMutation,
    useDeleteCouponMutation,
    useGetShopSettingsQuery,
    useUpdateShopSettingsMutation,
    useGetAffiliateDashboardQuery,
    useGetAffiliatesQuery,
    useGetAffiliateQuery,
    useAdjustAffiliateWalletMutation,
    useSetAffiliateStatusMutation,
    useGetCommissionsQuery,
    useLazyGetCommissionsQuery,
    useLazyGetAffiliatesQuery,
    useGetAffiliateSettingsQuery,
    useUpdateAffiliateSettingsMutation,
    useGetWithdrawalsQuery,
    useRejectWithdrawalMutation,
    useCreateWeeklyPayoutBatchMutation,
    useGetPayoutBatchesQuery,
    useGetPayoutBatchQuery,
    useMarkWithdrawalPaidMutation,
    useFailWithdrawalMutation,
    useResolveLegacyWithdrawalMutation,
    useGetMilestoneTiersQuery,
    useCreateMilestoneTierMutation,
    useUpdateMilestoneTierMutation,
    useDeleteMilestoneTierMutation,
    useGetMilestoneRewardsQuery,
    useUpdateMilestoneRewardMutation,
} = shopAdminApis;

export default shopAdminApis;
